import { useState, useEffect, useRef, useCallback } from 'react';
import { collection, query, where, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { getFailoverAwareDatabase, getFailoverAwareAuth } from '../config/firebaseWithFailover';
import { onAuthStateChanged } from 'firebase/auth';
import { 
  getNotificationState, 
  updatePageVisit, 
  updateLoadVisit,
  isNewerThanPageVisit,
  isNewerThanLoadVisit,
  isLoadSpecificNotification,
  getLoadIdFromNotification,
  markNotificationsAsRead,
  markNotificationDocsAsRead,
  markLoadNotificationsAsRead,
  cleanupReadNotifications,
  NotificationState,
  Notification
} from '../services/notificationTrackingService';
import Logger from '../services/logger';
import {
  getNotificationTypesForSidebarPage,
  mergePageVisitsPreferNewer,
  normalizeNotificationCreatedAt,
  CarrierSidebarPage,
} from '../constants/notificationSidebarAllocation';
import { computeCarrierIndicatorCounts } from '../services/bellSidebarIndicatorCounts';
import { isOperationsNotificationType } from '../constants/notificationChannels';

interface NotificationCounts {
  myLoads: number;
  availableLoads: number;
  partners: number;
  payments: number;
  bellOnly: number;
  total: number;
  loadSpecific: { [loadId: string]: number };
}

export const useEnhancedCarrierNotifications = () => {
  const [counts, setCounts] = useState<NotificationCounts>({
    myLoads: 0,
    availableLoads: 0,
    partners: 0,
    payments: 0,
    bellOnly: 0,
    total: 0,
    loadSpecific: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notificationState, setNotificationState] = useState<NotificationState | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const notificationsRef = useRef<Notification[]>([]);

  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  // Load notification state when user changes
  useEffect(() => {
    const auth = getFailoverAwareAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          setLoading(true);
          setError(null);
          
          const state = await getNotificationState(user.uid);
          setNotificationState((prev) => {
            if (!prev) return state;
            return {
              ...state,
              pageVisits: mergePageVisitsPreferNewer(prev.pageVisits, state.pageVisits) as NotificationState['pageVisits'],
              loadVisits: { ...(state.loadVisits || {}), ...(prev.loadVisits || {}) },
            };
          });
          
          cleanupReadNotifications(user.uid).catch(err => 
            Logger.warn('Failed to cleanup old notifications:', { err })
          );
          
        } catch (err) {
          Logger.error('Error loading notification state:', err instanceof Error ? err : new Error(String(err)), { err });
          setError('Failed to load notification state');
          setLoading(false);
        }
      } else {
        setNotificationState(null);
        setNotifications([]);
        notificationsRef.current = [];
        setCounts({
          myLoads: 0,
          availableLoads: 0,
          partners: 0,
          payments: 0,
          bellOnly: 0,
          total: 0,
          loadSpecific: {}
        });
        setLoading(false);
        setError(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // Listen to notifications when user and state are available
  useEffect(() => {
    const auth = getFailoverAwareAuth();
    if (!auth.currentUser || !notificationState) return;

    const db = getFailoverAwareDatabase();
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('recipientId', '==', auth.currentUser.uid),
      where('read', '==', false),
      orderBy('createdAt', 'desc'),
      limit(100)
    );

    const unsubscribeNotifications = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        const nextNotifications: Notification[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          nextNotifications.push({
            ...data,
            id: doc.id,
            type: data.type || 'unknown',
            read: data.read === true,
            createdAt: normalizeNotificationCreatedAt(data.createdAt),
            loadId: data.loadId,
            poNumber: data.poNumber,
          });
        });
        setNotifications(nextNotifications);
        notificationsRef.current = nextNotifications;
        setLoading(false);
      },
      (error) => {
        Logger.error('Error fetching notifications:', error instanceof Error ? error : new Error(String(error)), { error });
        setError('Failed to load notifications');
        setLoading(false);
      }
    );

    return () => unsubscribeNotifications();
  }, [notificationState]);

  useEffect(() => {
    if (!notificationState) {
      setCounts({
        myLoads: 0,
        availableLoads: 0,
        partners: 0,
        payments: 0,
        bellOnly: 0,
        total: 0,
        loadSpecific: {},
      });
      return;
    }

    const baseCounts = computeCarrierIndicatorCounts(
      notifications,
      notificationState.pageVisits
    );

    const newCounts: NotificationCounts = {
      ...baseCounts,
      loadSpecific: {},
    };

    notifications.forEach((notification) => {
      if (!isOperationsNotificationType(notification.type, 'carrier')) {
        return;
      }

      if (isLoadSpecificNotification(notification)) {
        const loadId = getLoadIdFromNotification(notification);
        if (loadId) {
          const lastLoadVisit = notificationState.loadVisits[loadId];
          const isNewForLoad = isNewerThanLoadVisit(notification, lastLoadVisit);
          if (isNewForLoad) {
            newCounts.loadSpecific[loadId] = (newCounts.loadSpecific[loadId] || 0) + 1;
          }
        }
      }
    });

    setCounts(newCounts);
  }, [notifications, notificationState]);

  const markPageVisited = useCallback(async (pageType: keyof NotificationState['pageVisits']) => {
    const auth = getFailoverAwareAuth();
    if (!auth.currentUser) return;

    try {
      const now = new Date();

      setNotificationState((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          pageVisits: {
            ...prev.pageVisits,
            [pageType]: now,
          },
        };
      });

      await updatePageVisit(auth.currentUser.uid, pageType);

      const typesForPage = new Set(getNotificationTypesForSidebarPage(pageType));
      if (typesForPage.size > 0) {
        const ids = notificationsRef.current
          .filter((n) => typesForPage.has(n.type) && n.read !== true)
          .map((n) => n.id);
        await markNotificationDocsAsRead(ids);
      }
      await markNotificationsAsRead(auth.currentUser.uid, pageType);
    } catch (error) {
      Logger.error(
        `Error marking ${pageType} as visited:`,
        error instanceof Error ? error : new Error(String(error)),
        { error, pageType }
      );
      setNotificationState((prev) => {
        if (!prev) return prev;
        getNotificationState(auth.currentUser!.uid)
          .then((state) => {
            setNotificationState(state);
          })
          .catch((err) => {
            Logger.error(
              'Error reloading notification state:',
              err instanceof Error ? err : new Error(String(err)),
              { err }
            );
          });
        return prev;
      });
    }
  }, []);

  const markLoadVisited = async (loadId: string) => {
    const auth = getFailoverAwareAuth();
    if (!auth.currentUser) return;
    
    try {
      await updateLoadVisit(auth.currentUser.uid, loadId);
      await markLoadNotificationsAsRead(auth.currentUser.uid, loadId);
      
      setNotificationState(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          loadVisits: {
            ...prev.loadVisits,
            [loadId]: new Date()
          }
        };
      });
    } catch (error) {
      Logger.error(`Error marking load ${loadId} as visited:`, error instanceof Error ? error : new Error(String(error)), { error, loadId });
    }
  };

  return {
    counts,
    loading,
    error,
    hasAnyNotifications: counts.total > 0 || counts.bellOnly > 0,
    hasSidebarNotifications: counts.myLoads > 0 || counts.availableLoads > 0 || counts.partners > 0 || counts.payments > 0,
    hasBellNotifications: counts.bellOnly > 0,
    markPageVisited,
    markLoadVisited,
    getLoadNotificationCount: (loadId: string) => counts.loadSpecific[loadId] || 0
  };
};

export default useEnhancedCarrierNotifications;
